import { createApplicationRequests, type ApplicationRequests } from './application-requests';
import { matchesInteractionReceipt } from './interaction-receipt';
export { matchesInteractionReceipt } from './interaction-receipt';
import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { normalizeResolution, normalizeResponse } from './normalized-activity';
import {
  type InteractionResolvedMessage,
} from '@borgee/agent-remote-protocol';
import type { InteractionResponseFrame } from '../types/ws-frames';

export class InteractionResponseError extends Error {
  constructor(readonly messageDescriptor: MessageDescriptor, readonly code?: string, readonly details?: unknown, options?: ErrorOptions, diagnostic?: string) {
    super(diagnostic ?? messageDescriptor.message ?? messageDescriptor.id, options);
    this.name = 'InteractionResponseError';
  }
}

const responseErrors: Record<string, MessageDescriptor> = {
  INVALID_REQUEST: msg`The interaction response is invalid or too large. Shorten the answer and try again.`,
  BUSY: msg`Too many interaction responses are pending.`,
  FORBIDDEN: msg`Only the agent owner can respond.`,
  NOT_FOUND: msg`This interaction is no longer available.`,
  AGENT_OFFLINE: msg`The agent is disconnected.`,
  TIMEOUT: msg`Response confirmation timed out. The answer may have been accepted.`,
  INTERACTION_REJECTED: msg`The agent could not accept this response. The request may already be resolved.`,
  INVALID_RESULT: msg`The agent returned an invalid interaction result.`,
};

type Submission = Omit<InteractionResponseFrame, 'type' | 'client_request_id'>;
type Socket = Pick<WebSocket, 'readyState' | 'send'>;
interface Attempt {
  input: Submission;
  resolve: (result: InteractionResolvedMessage) => void;
  reject: (error: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}
interface InteractionConnection {
  submit(input: Submission): Promise<InteractionResolvedMessage>;
  receive(frame: unknown): void;
  close(): void;
}
let active: InteractionConnection | undefined;

export function submitInteractionResponse(input: Submission): Promise<InteractionResolvedMessage> {
  return active?.submit(input) ?? Promise.reject(new InteractionResponseError(msg`Connection is disconnected.`));
}

/** One socket owns all attempts; a replacement retires them without resending. */
export function bindInteractionResponseSocket(socket: Socket, sharedRequests?: ApplicationRequests): InteractionConnection {
  active?.close();
  const requests = sharedRequests ?? createApplicationRequests(socket);
  if (!sharedRequests) requests.enable(true);
  const controllers = new Set<AbortController>();
  const attempts = new Map<string, Attempt>();
  let closed = false;
  function retire(id: string): Attempt | undefined {
    const attempt = attempts.get(id);
    if (attempt) { clearTimeout(attempt.timer); attempts.delete(id); }
    return attempt;
  }
  const connection: InteractionConnection = {
    submit(input) {
      if (closed || socket.readyState !== 1) return Promise.reject(new InteractionResponseError(msg`Connection is disconnected.`));
      const body = normalizeResponse(input.body);
      if (!input.channel_id || (!input.task_id && !input.binding_id) || !body) return Promise.reject(new InteractionResponseError(msg`The interaction response is invalid.`));
      input = { ...input, body };
      if (input.binding_id) {
        const captured = structuredClone(input);
        const controller = new AbortController();
        controllers.add(controller);
        const expiresAt = Date.now() + 15_000;
        const timer = setTimeout(() => controller.abort(), Math.max(0, expiresAt - Date.now()));
        return requests.request(captured.channel_id, body.payload.agentId, 'interaction_response', captured, { signal: controller.signal, expiresAt }).then(value => {
          const result = normalizeResolution(value);
          const original = captured.body.payload;
          if (!result || result.payload.agentId !== original.agentId || result.payload.requestId !== original.requestId
            || !matchesInteractionReceipt(original.response, result.payload.response)) throw new InteractionResponseError(msg`The Agent returned an invalid interaction result.`);
          return result;
        }).catch((cause: unknown) => {
          if (cause instanceof InteractionResponseError) throw cause;
          const code = cause instanceof Error ? cause.message : 'application_request_unconfirmed';
          const descriptor = code === 'application_request_too_large'
            ? msg`The interaction response is too large for this connection. It was not sent; the original request is unchanged.`
            : ['application_request_aborted', 'application_request_unconfirmed', 'application_connection_closed', 'application_channel_released'].includes(code)
              ? msg`Response confirmation timed out. The answer may have been accepted.`
              : code === 'invalid_application_response'
                ? msg`The Agent returned an invalid interaction result.`
                : msg`The control result is unconfirmed. Check the session state before trying again. Your draft is preserved.`;
          throw new InteractionResponseError(descriptor, code, undefined, { cause });
        }).finally(() => { clearTimeout(timer); controllers.delete(controller); });
      }
      if (attempts.size >= 4) return Promise.reject(new InteractionResponseError(msg`Too many interaction responses are pending.`));
      const id = crypto.randomUUID();
      const frame = JSON.stringify({ ...input, type: 'interaction_response', client_request_id: id });
      if (new TextEncoder().encode(frame).byteLength > 32768) return Promise.reject(new InteractionResponseError(msg`The interaction response is too large for this connection. It was not sent; the original request is unchanged.`));
      return new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
          retire(id)?.reject(new InteractionResponseError(msg`Response confirmation timed out. The answer may have been accepted.`));
        }, 15_000);
        attempts.set(id, { input: structuredClone(input), resolve, reject, timer });
        try { socket.send(frame); }
        catch (cause) { retire(id)?.reject(new InteractionResponseError(msg`Response could not be sent. The answer may have been accepted.`, undefined, undefined, { cause })); }
      });
    },
    receive(value) {
      if (closed || typeof value !== 'object' || value === null) return;
      const frame = value as Record<string, unknown>;
      if (frame.type === 'agent_response') { requests.receive(frame); return; }
      if (!['interaction_response_result', 'agent_response'].includes(String(frame.type)) || typeof frame.client_request_id !== 'string') return;
      const pending = attempts.get(frame.client_request_id);
      if (!pending || (pending.input.binding_id ? frame.type !== 'agent_response' : frame.type !== 'interaction_response_result')) return;
      const attempt = retire(frame.client_request_id);
      if (!attempt) return;
      const invalid = () => attempt.reject(new InteractionResponseError(msg`The Agent returned an invalid interaction result.`));
      if (attempt.input.binding_id && (frame.channel_id !== attempt.input.channel_id || frame.agent_id !== attempt.input.body.payload.agentId)) { invalid(); return; }
      const payloadKey = attempt.input.binding_id ? 'body' : 'data';
      if (Object.hasOwn(frame, payloadKey) === Object.hasOwn(frame, 'error')) { invalid(); return; }
      if (Object.hasOwn(frame, 'error')) {
        const error = frame.error as Record<string, unknown> | null;
        if (!error || typeof error.code !== 'string' || !error.code || typeof error.message !== 'string' || !error.message || error.message.length > 512) { invalid(); return; }
        const code = error.code;
        const descriptor = Object.hasOwn(responseErrors, code) ? responseErrors[code]! : msg`The control result is unconfirmed. Check the session state before trying again. Your draft is preserved.`;
        attempt.reject(new InteractionResponseError(descriptor, code, error, undefined, error.message));
        return;
      }
      const result = normalizeResolution(frame[payloadKey]);
      const original = attempt.input.body.payload;
      if (!result || result.payload.agentId !== original.agentId || result.payload.requestId !== original.requestId
        || !matchesInteractionReceipt(original.response, result.payload.response)) { invalid(); return; }
      attempt.resolve(result);
    },
    close() {
      if (closed) return;
      closed = true;
      for (const controller of controllers) controller.abort();
      controllers.clear();
      if (!sharedRequests) requests.close();
      if (active === connection) active = undefined;
      for (const id of attempts.keys()) retire(id)?.reject(new InteractionResponseError(msg`Connection is disconnected. The answer may have been accepted.`));
    },
  };
  active = connection;
  return connection;
}
